import {
  Avatar,
  List,
  ListItemButton,
  LoadingSpinner,
  Text,
} from "@clipboard-health/ui-components";
import { Input, Link, Stack } from "@mui/material";
import { hideKeyboardOnPressEnter } from "@src/appV2/lib/utils/capacitor";
import { WORK_WITH_FRIENDS_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { useGetFriendEligibilities } from "../../../../Shifts/ShiftTransfer/api/useGetFriendEligibilities";
import { ButtonInternalLink } from "../../../components/ButtonInternalLink";

export interface ChosenFriend {
  id: string;
  attributes: {
    firstName: string;
    lastName: string;
    profileImageUrl?: string | undefined;
  };
}

export interface EligibleFriendChooserProps {
  shiftId: string;
  onFriendChosen: (friend: ChosenFriend) => void;
}

export function EligibleFriendChooser(props: EligibleFriendChooserProps) {
  const { shiftId, onFriendChosen } = props;

  const [searchString, setSearchString] = useState("");

  const history = useHistory();

  const { data, isLoading } = useGetFriendEligibilities(shiftId);

  const filteredFriends = useMemo(
    () =>
      data?.data
        .filter((element) =>
          `${element.attributes.firstName} ${element.attributes.lastName}`
            .toLowerCase()
            .includes(searchString.toLowerCase())
        )
        .sort((element1, element2) =>
          element1.attributes.eligible && !element2.attributes.eligible ? -1 : 1
        ),
    [data, searchString]
  );

  if (isLoading) {
    return (
      <Stack alignItems="center">
        <LoadingSpinner size="large" variant="primary" />
      </Stack>
    );
  }

  return (
    <Stack spacing={5}>
      <Text>
        You can only transfer shifts to professionals who are Enrolled, have the required documents
        and license, and are not on the DNR list of the facility.
      </Text>
      <Input
        fullWidth
        aria-label="Search friends"
        size="medium"
        type="search"
        placeholder="Search friends"
        value={searchString}
        onChange={(event) => {
          setSearchString(event.target.value);
        }}
        onKeyPress={hideKeyboardOnPressEnter}
      />
      <Text>
        Don&apos;t see your friend? Add them{" "}
        <Link
          onClick={() => {
            history.replace(WORK_WITH_FRIENDS_FULL_PATH);
          }}
        >
          here
        </Link>
      </Text>

      <List>
        {filteredFriends?.map((friend) => {
          return (
            <ListItemButton
              key={`friend-${friend.id}`}
              divider
              hideCallToActionIcon
              disabled={!friend.attributes.eligible}
              LinkComponent={ButtonInternalLink}
              onClick={() => {
                onFriendChosen?.(friend);
              }}
            >
              <Stack direction="row" spacing={5} alignItems="center" sx={{ flex: 1 }}>
                <Avatar
                  src={friend.attributes.profileImageUrl}
                  fallbackText={
                    friend.attributes.firstName.slice(0, 1) + friend.attributes.lastName.slice(0, 1)
                  }
                />
                <Stack spacing={2}>
                  <Text>{`${friend.attributes.firstName} ${friend.attributes.lastName}`}</Text>
                  {friend.attributes.eligible ? (
                    <Text variant="caption" color={(theme) => theme.palette.success.main}>
                      Eligible
                    </Text>
                  ) : (
                    <Text variant="caption" color="error">
                      Not Eligible
                    </Text>
                  )}
                </Stack>
              </Stack>
            </ListItemButton>
          );
        })}
      </List>
    </Stack>
  );
}
